/**
 * HistogramControl
 *
 * Histogram chart for distribution analysis
 * Auto-bins continuous data
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

const HistogramControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const histogramConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      dataKey: (config?.dataKey as string) || 'value',
      bins: (config?.bins as number) || 10,
      showFrequency: config?.showFrequency !== false,
    };
  }, [control.config]);

  const renderHistogram = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract values
    const values: number[] = [];
    chartData.forEach((d) => {
      const val = (d as Record<string, unknown>)[histogramConfig.dataKey];
      if (typeof val === 'number') values.push(val);
    });

    if (values.length === 0) return null;

    // Calculate bins
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue || 1;
    const binSize = range / histogramConfig.bins;

    // Create bin data
    const bins: number[] = Array(histogramConfig.bins).fill(0);
    values.forEach((val) => {
      const binIndex = Math.min(
        Math.floor((val - minValue) / binSize),
        histogramConfig.bins - 1,
      );
      bins[binIndex]++;
    });

    const maxFrequency = Math.max(...bins, 1);

    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    const barWidth = plotWidth / histogramConfig.bins;
    const yScale = plotHeight / maxFrequency;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * plotHeight;
          return (
            <line
              key={`grid-${ratio}`}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Y axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const freq = Math.round(ratio * maxFrequency);
          const y = height - padding - ratio * plotHeight;
          return (
            <text
              key={`y-label-${ratio}`}
              x={padding - 10}
              y={y}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              {freq}
            </text>
          );
        })}

        {/* Axes */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Bars */}
        {bins.map((frequency, binIndex) => {
          const barHeight = (frequency / maxFrequency) * plotHeight;
          const x = padding + binIndex * barWidth;
          const y = height - padding - barHeight;

          const binMin = minValue + binIndex * binSize;
          const binMax = binMin + binSize;

          return (
            <g key={`bin-${binIndex}`}>
              <rect
                x={x + 2}
                y={y}
                width={barWidth - 4}
                height={barHeight}
                fill="#8884d8"
                opacity="0.8"
              />
              {binIndex % 2 === 0 && (
                <text
                  x={x + barWidth / 2}
                  y={height - padding + 20}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#6b7280"
                >
                  {binMin.toFixed(0)}
                </text>
              )}
              {histogramConfig.showFrequency && frequency > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#374151"
                >
                  {frequency}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  }, [chartData, histogramConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="histogram">
      <div style={{ width: '100%', height: '100%' }}>{renderHistogram}</div>
    </BaseChartComponent>
  );
});

HistogramControl.displayName = 'HistogramControl';
export default HistogramControl;
